import {
  FETCH_SUBSCRIPTION_DATA_REQUEST,
  FETCH_SUBSCRIPTION_DATA_SUCCESS,
  FETCH_SUBSCRIPTION_DATA_FAILURE,
  FETCH_SUBSCRIPTION_PLANS_REQUEST,
  FETCH_SUBSCRIPTION_PLANS_SUCCESS,
  FETCH_SUBSCRIPTION_PLANS_FAILURE,
  SELECT_SUBSCRIPTION_PLAN,
  CREATE_ORDER_REQUEST,
  CREATE_ORDER_SUCCESS,
  CREATE_ORDER_FAILURE,
  CLEAR_ORDER_DATA,
  VERIFY_PAYMENT_REQUEST,
  VERIFY_PAYMENT_SUCCESS,
  VERIFY_PAYMENT_FAILURE,
  OPEN_RAZORPAY_CHECKOUT,
  RAZORPAY_CHECKOUT_SUCCESS,
  RAZORPAY_CHECKOUT_FAILURE,
  CLOSE_SUCCESS_MODAL,
  FETCH_BLOCKED_USERS_LIST_REQUEST,
  FETCH_BLOCKED_USERS_LIST_SUCCESS,
  FETCH_BLOCKED_USERS_LIST_FAILURE,
} from './constants';
import { httpGet, httpPost } from '../../utils/http';
import { showErrorMsg, showSuccessMsg } from '../../utils/notifications';

// Fetch subscription data for logged-in users
export const fetchSubscriptionDataRequest = () => ({
  type: FETCH_SUBSCRIPTION_DATA_REQUEST,
});

export const fetchSubscriptionDataSuccess = (data) => ({
  type: FETCH_SUBSCRIPTION_DATA_SUCCESS,
  payload: data,
});

export const fetchSubscriptionDataFailure = (error) => ({
  type: FETCH_SUBSCRIPTION_DATA_FAILURE,
  payload: error,
});

export const fetchSubscriptionData = () => async (dispatch) => {
  dispatch(fetchSubscriptionDataRequest());
  try {
    const response = await httpGet('/subscription/my-subscription');
    if (response.success && response.data) {
      // Transform the new API format to the format expected by UI
      const data = response.data;
      
      // Transform available plans to the format expected by UI
      let transformedPlans = [];
      if (data.available_plans) {
        transformedPlans = data.available_plans.map(plan => {
          const actualAmount = Math.round(plan.base_price / 100); // Convert paise to rupees
          
          // Parse meta JSON string
          let meta = {};
          try {
            meta = plan.meta ? JSON.parse(plan.meta) : {};
          } catch (e) {
            console.warn('Failed to parse meta:', plan.meta);
            meta = {};
          }
          
          const discount = meta.discount || 0;
          const discountAmount = Math.round((actualAmount * discount) / 100);
          const finalAmount = actualAmount - discountAmount;
          
          return {
            id: plan.id,
            text: plan.description, // Use description as text
            actual_amount: actualAmount,
            discount: discount,
            final_amount: finalAmount,
            // Keep original data for reference
            original_plan: plan
          };
        });
      }
      
      // Convert UTC end_date to IST and format
      let subscriptionEndDate = null;
      if (data.active_subscription && data.active_subscription.end_date) {
        const utcDate = new Date(data.active_subscription.end_date);
        const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000)); // Convert to IST
        
        // Format as "dd MMM yyyy" (e.g., "15 Jan 2024")
        const months = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
        const day = istDate.getDate().toString().padStart(2, '0');
        const month = months[istDate.getMonth()];
        const year = istDate.getFullYear();
        subscriptionEndDate = `${day} ${month} ${year}`;
      }
      
      // Transform to the format expected by UI
      const transformedData = {
        is_subscribed: data.active_subscription && data.active_subscription.status === 'ACTIVE',
        subscription_end_date: subscriptionEndDate,
        subscription_plans: transformedPlans,
        // Keep original data for reference
        original_data: data
      };
      
      dispatch(fetchSubscriptionDataSuccess(transformedData));
    } else {
      dispatch(fetchSubscriptionDataFailure(response.message || 'Failed to fetch subscription data'));
      showErrorMsg(response.message || 'Failed to fetch subscription data');
    }
  } catch (error) {
    dispatch(fetchSubscriptionDataFailure(error.message));
    showErrorMsg('Failed to fetch subscription data');
  }
};

// Fetch subscription plans for non-logged-in users
export const fetchSubscriptionPlansRequest = () => ({
  type: FETCH_SUBSCRIPTION_PLANS_REQUEST,
});

export const fetchSubscriptionPlansSuccess = (plans) => ({
  type: FETCH_SUBSCRIPTION_PLANS_SUCCESS,
  payload: plans,
});

export const fetchSubscriptionPlansFailure = (error) => ({
  type: FETCH_SUBSCRIPTION_PLANS_FAILURE,
  payload: error,
});

export const fetchSubscriptionPlans = () => async (dispatch) => {
  dispatch(fetchSubscriptionPlansRequest());
  try {
    const response = await httpGet('/subscription/plans');
    if (response.success && response.data) {
      // Transform the new API format to the format expected by UI
      const transformedPlans = response.data.map(plan => {
        const actualAmount = Math.round(plan.base_price / 100); // Convert paise to rupees
        
        // Parse meta JSON string
        let meta = {};
        try {
          meta = plan.meta ? JSON.parse(plan.meta) : {};
        } catch (e) {
          console.warn('Failed to parse meta:', plan.meta);
          meta = {};
        }
        
        const discount = meta.discount || 0;
        const discountAmount = Math.round((actualAmount * discount) / 100);
        const finalAmount = actualAmount - discountAmount;
        
        return {
          id: plan.id,
          text: plan.description, // Use description as text
          actual_amount: actualAmount,
          discount: discount,
          final_amount: finalAmount,
          // Keep original data for reference
          original_plan: plan
        };
      });
      
      dispatch(fetchSubscriptionPlansSuccess(transformedPlans));
    } else {
      dispatch(fetchSubscriptionPlansFailure(response.message || 'Failed to fetch subscription plans'));
      showErrorMsg(response.message || 'Failed to fetch subscription plans');
    }
  } catch (error) {
    dispatch(fetchSubscriptionPlansFailure(error.message));
    showErrorMsg('Failed to fetch subscription plans');
  }
};

// Select a subscription plan
export const selectSubscriptionPlan = (plan) => ({
  type: SELECT_SUBSCRIPTION_PLAN,
  payload: plan,
});


// Create Order Actions
export const createOrderRequest = () => ({
  type: CREATE_ORDER_REQUEST,
});

export const createOrderSuccess = (data) => ({
  type: CREATE_ORDER_SUCCESS,
  payload: data,
});

export const createOrderFailure = (error) => ({
  type: CREATE_ORDER_FAILURE,
  payload: error,
});

// Create Razorpay order
export const createOrder = (planId, promotionalCode = null, notes = {}) => async (dispatch) => {
  dispatch(createOrderRequest());
  try {
    const requestData = {
      subscription_plan_id: planId,
      ...(promotionalCode && { promotional_code: promotionalCode }),
      ...(Object.keys(notes).length > 0 && { notes })
    };
    
    const response = await httpPost('/subscription/purchase', requestData);
    if (response.success && response.data) {
      // Transform response to format expected by Razorpay integration
      const transformedData = {
        success: true,
        amount: response.data.amount,
        currency: response.data.currency,
        name: "Pukaar AI",
        order_id: response.data.order_id,
        key_id: response.data.key_id,
        subscription_id: response.data.subscription_id,
        error_msg: null
      };
      
      dispatch(createOrderSuccess(transformedData));
      return transformedData;
    } else {
      dispatch(createOrderFailure(response.message));
      showErrorMsg(response.message || 'Failed to create order');
      return null;
    }
  } catch (error) {
    dispatch(createOrderFailure(error.message));
    showErrorMsg('Failed to create order');
    return null;
  }
};

// Verify Payment Actions
export const verifyPaymentRequest = () => ({
  type: VERIFY_PAYMENT_REQUEST,
});

export const verifyPaymentSuccess = (data) => ({
  type: VERIFY_PAYMENT_SUCCESS,
  payload: data,
});

export const verifyPaymentFailure = (error) => ({
  type: VERIFY_PAYMENT_FAILURE,
  payload: error,
});

// Verify payment with backend
export const verifyPayment = (paymentData) => async (dispatch) => {
  dispatch(verifyPaymentRequest());
  try {
    const response = await httpPost('/payment/verify-payment', paymentData);
    
    if (response.success) {
      dispatch(verifyPaymentSuccess(response));
      showSuccessMsg('Payment successful! Your subscription is now active.');
      // Refresh subscription data
      dispatch(fetchSubscriptionData());
      return true;
    } else {
      dispatch(verifyPaymentFailure(response.message));
      showErrorMsg('Payment verification failed: ' + response.message);
      return false;
    }
  } catch (error) {
    console.error('Payment verification error:', error);
    dispatch(verifyPaymentFailure(error.message));
    showErrorMsg('Payment verification failed. Please contact support.');
    return false;
  }
};

// Clear order data action
export const clearOrderData = () => ({
  type: CLEAR_ORDER_DATA,
});

// Razorpay Checkout Actions
export const openRazorpayCheckout = (orderData, onSuccess, onFailure) => ({
  type: OPEN_RAZORPAY_CHECKOUT,
  payload: { orderData, onSuccess, onFailure },
});

export const razorpayCheckoutSuccess = (data) => ({
  type: RAZORPAY_CHECKOUT_SUCCESS,
  payload: data,
});

export const razorpayCheckoutFailure = (error) => ({
  type: RAZORPAY_CHECKOUT_FAILURE,
  payload: error,
});

// Open Razorpay checkout
export const openRazorpay = (orderData, onSuccess, onFailure) => (dispatch) => {
  // Dispatch action to update state
  dispatch(openRazorpayCheckout(orderData, onSuccess, onFailure));
  
  // Check if Razorpay is loaded
  if (!window.Razorpay) {
    console.log('Razorpay not loaded');
    dispatch(razorpayCheckoutFailure('Razorpay not loaded'));
    showErrorMsg('Payment system not ready. Please refresh and try again.');
    return;
  }

  const options = {
    key: orderData.key_id, // Use key_id from response or fallback
    amount: orderData.amount,
    currency: orderData.currency,
    name: orderData.name,
    description: `Premium Subscription`,
    image: "/images/pukaar-ai-icon.png",
    order_id: orderData.order_id,
    handler: async function (paymentResponse) {
      console.log('Payment successful:', paymentResponse);
      dispatch(razorpayCheckoutSuccess(paymentResponse));
      if (onSuccess) onSuccess(paymentResponse);
    },
    prefill: {
      name: 'User',
      email: '',
      contact: ''
    },
    theme: {
      color: "#734388"
    },
    modal: {
      ondismiss: function() {
        console.log('Checkout dismissed by user');
        dispatch(razorpayCheckoutFailure('Payment cancelled by user'));
        // Clear order data when Razorpay window is closed
        dispatch(clearOrderData());
        // Call the onFailure callback to reset loading state
        if (onFailure) onFailure('Payment cancelled by user');
      }
    }
  };

  console.log('Creating Razorpay instance with options:', options);
  const rzp = new window.Razorpay(options);
  
  rzp.on('payment.failed', function (response) {
    console.log('Payment failed:', response);
    dispatch(razorpayCheckoutFailure(response.error.description));
    showErrorMsg('Payment failed: ' + response.error.description);
    // Clear order data when payment fails
    dispatch(clearOrderData());
    if (onFailure) onFailure(response);
  });

  console.log('Opening Razorpay checkout...');
  rzp.open();
};

// Close success modal action
export const closeSuccessModal = () => ({
  type: CLOSE_SUCCESS_MODAL,
});

// Blocked Users List Actions
export const fetchBlockedUsersListRequest = () => ({
  type: FETCH_BLOCKED_USERS_LIST_REQUEST,
});

export const fetchBlockedUsersListSuccess = (data) => ({
  type: FETCH_BLOCKED_USERS_LIST_SUCCESS,
  payload: data,
});

export const fetchBlockedUsersListFailure = (error) => ({
  type: FETCH_BLOCKED_USERS_LIST_FAILURE,
  payload: error,
});

// Fetch blocked users list
export const fetchBlockedUsersList = () => async (dispatch) => {
  dispatch(fetchBlockedUsersListRequest());
  try {
    const response = await httpGet('/subscription/blocked-users-list');
    
    if (response.success && response.data) {
      dispatch(fetchBlockedUsersListSuccess(response.data));
    } else {
      dispatch(fetchBlockedUsersListFailure(response.message || 'Failed to fetch blocked users list'));
    }
  } catch (error) {
    dispatch(fetchBlockedUsersListFailure(error.message));
  }
};


